// Aba "Leads" de Configurações (super admin): token do Apify e limites,
// pacotes, e as buscas de todas as empresas com custo real.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS } from "../../styles/aerosTokens";
import LeadsPacotesManager from "../LeadsPacotesManager";
import { statusLabel, formatMilhar } from "../../pages/Leads/leadsRules";

const GRID = "minmax(0,1.2fr) minmax(0,1.4fr) 90px 90px 90px 100px 130px";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    root: { display: "flex", flexDirection: "column", gap: 16 },
    card: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${border}`, borderRadius: 14, minWidth: 0 },
    cardPad: { padding: "16px 18px" },
    title: { fontSize: 13, fontWeight: 700, color: theme.palette.text.primary, marginBottom: 12 },
    form: { display: "grid", gridTemplateColumns: "minmax(0,2fr) 120px 120px 120px 120px", gap: 10, alignItems: "end", [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr 1fr" }, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
    hint: { fontSize: 11.5, color: theme.palette.aeros.textTertiary, marginTop: 6 },
    botao: { borderRadius: 9, fontWeight: 700, fontSize: 12.5, textTransform: "none", background: `linear-gradient(135deg, ${AEROS.defaultPrimary}, ${AEROS.cyan})`, color: "#07080D", padding: "9px 12px", "&.Mui-disabled": { opacity: 0.5, color: "#07080D" } },
    tableScroll: { overflowX: "auto" },
    rowHead: { minWidth: 820, display: "grid", gridTemplateColumns: GRID, gap: 8, padding: "10px 16px", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary, borderBottom: `1px solid ${border}` },
    row: { minWidth: 820, display: "grid", gridTemplateColumns: GRID, gap: 8, padding: "10px 16px", borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.04)"}`, alignItems: "center", fontSize: 12.5 },
    cell: { minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: theme.palette.text.primary },
    num: { textAlign: "right", fontVariantNumeric: "tabular-nums", color: theme.palette.text.primary },
    vazio: { padding: "20px 16px", textAlign: "center", fontSize: 12.5, color: theme.palette.aeros.textTertiary },
  };
});

const LeadsAdminPanel = () => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [cfg, setCfg] = useState({ fatorSobrebusca: 1.4, diasBloqueioRepeticao: 30, maxPorBusca: 500, apifyTokenConfigurado: false });
  const [token, setToken] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [buscas, setBuscas] = useState([]);

  const carrega = useCallback(async () => {
    try {
      const [{ data: c }, { data: b }] = await Promise.all([api.get("/leads/admin/config"), api.get("/leads/admin/buscas")]);
      setCfg(c); setBuscas(b.buscas || []);
    } catch (err) { toastError(err); }
  }, []);
  useEffect(() => { carrega(); }, [carrega]);

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      const body = { fatorSobrebusca: Number(cfg.fatorSobrebusca), diasBloqueioRepeticao: Number(cfg.diasBloqueioRepeticao), maxPorBusca: Number(cfg.maxPorBusca) };
      if (token.trim()) body.apifyToken = token.trim();
      const { data } = await api.put("/leads/admin/config", body);
      setCfg(data); setToken("");
      toast.success(i18n.t("aeros.leadsAdmin.saved"));
    } catch (err) { toastError(err); }
    setSalvando(false);
  };

  return (
    <div className={classes.root}>
      <div className={`${classes.card} ${classes.cardPad}`}>
        <div className={classes.title}>{i18n.t("aeros.leadsAdmin.config")}</div>
        <div className={classes.form}>
          <div>
            <label className={m.fieldLabel}>{i18n.t("aeros.leadsAdmin.apifyToken")}</label>
            <TextField fullWidth type="password" variant="outlined" className={`${m.input} ${m.inputSmall}`} value={token} onChange={(e) => setToken(e.target.value)} autoComplete="new-password" />
            <div className={classes.hint}>{cfg.apifyTokenConfigurado ? i18n.t("aeros.leadsAdmin.apifyTokenConfigured") : i18n.t("aeros.leadsAdmin.apifyTokenMissing")}</div>
          </div>
          <div><label className={m.fieldLabel}>{i18n.t("aeros.leadsAdmin.factor")}</label>
            <TextField fullWidth type="number" variant="outlined" className={`${m.input} ${m.inputSmall}`} value={cfg.fatorSobrebusca} onChange={(e) => setCfg({ ...cfg, fatorSobrebusca: e.target.value })} inputProps={{ min: 1, max: 3, step: "0.1" }} /></div>
          <div><label className={m.fieldLabel}>{i18n.t("aeros.leadsAdmin.blockDays")}</label>
            <TextField fullWidth type="number" variant="outlined" className={`${m.input} ${m.inputSmall}`} value={cfg.diasBloqueioRepeticao} onChange={(e) => setCfg({ ...cfg, diasBloqueioRepeticao: e.target.value })} inputProps={{ min: 0, max: 365 }} /></div>
          <div><label className={m.fieldLabel}>{i18n.t("aeros.leadsAdmin.maxPerSearch")}</label>
            <TextField fullWidth type="number" variant="outlined" className={`${m.input} ${m.inputSmall}`} value={cfg.maxPorBusca} onChange={(e) => setCfg({ ...cfg, maxPorBusca: e.target.value })} inputProps={{ min: 10, max: 5000 }} /></div>
          <Button className={classes.botao} onClick={handleSalvar} disabled={salvando}>{i18n.t("aeros.leadsAdmin.save")}</Button>
        </div>
        <div className={classes.hint}>{i18n.t("aeros.leadsAdmin.factorHint")}</div>
      </div>

      <LeadsPacotesManager />

      <div className={classes.card}>
        <div className={`${classes.title} ${classes.cardPad}`} style={{ marginBottom: 0 }}>{i18n.t("aeros.leadsAdmin.searches")}</div>
        <div className={classes.tableScroll}>
          <div className={classes.rowHead}>
            <div>{i18n.t("aeros.leadsAdmin.company")}</div><div>{i18n.t("aeros.leads.col.niche")}</div>
            <div style={{ textAlign: "right" }}>{i18n.t("aeros.leads.col.requested")}</div>
            <div style={{ textAlign: "right" }}>{i18n.t("aeros.leads.col.delivered")}</div>
            <div style={{ textAlign: "right" }}>{i18n.t("aeros.leadsAdmin.scraped")}</div>
            <div style={{ textAlign: "right" }}>{i18n.t("aeros.leadsAdmin.cost")}</div>
            <div>{i18n.t("aeros.leads.col.status")}</div>
          </div>
          {buscas.length === 0 ? <div className={classes.vazio}>{i18n.t("aeros.leadsAdmin.searchesEmpty")}</div> : buscas.map((b) => (
            <div className={classes.row} key={b.id} title={b.erro || ""}>
              <div className={classes.cell}>{b.company?.name || b.companyId}</div>
              <div className={classes.cell}>{b.nicho} · {b.cidade}/{b.estado} · {moment(b.createdAt).format("DD/MM HH:mm")}</div>
              <div className={classes.num}>{formatMilhar(b.quantidadeSolicitada)}</div>
              <div className={classes.num}>{formatMilhar(b.quantidadeEntregue)}</div>
              <div className={classes.num}>{b.lugaresRaspados === null || b.lugaresRaspados === undefined ? "—" : formatMilhar(b.lugaresRaspados)}</div>
              <div className={classes.num}>{b.custoApifyUsd === null || b.custoApifyUsd === undefined ? "—" : Number(b.custoApifyUsd).toFixed(3)}</div>
              <div className={classes.cell}>{statusLabel(b.status)}{b.erro ? ` · ${i18n.t("aeros.leadsAdmin.error")}` : ""}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeadsAdminPanel;
